import { syncAchievements } from '../services/achievementService';
import { soundService } from '../services/soundService';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback } from 'react';
import {
    Library, BookMarked, Languages, Flame, MessageSquareText,
    Crown, Users, Moon, Timer, Globe, Copy, Headphones,
    UserCheck, Heart, Loader2, Sunrise, Trophy, ChevronDown, Lock,
    PartyPopper
} from 'lucide-react';
import {
    ACHIEVEMENTS, AchievementDef, UserAchievement,
    loadAchievements, getLevelFromProgress, getNextThreshold,
    trackAchievement
} from '../services/achievementService';
import { motion, AnimatePresence } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1] as const;

// ── Icon resolver ─────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.FC<any>> = {
    Library, BookMarked, Languages, Flame, MessageSquareText,
    Crown, Users, Sunrise, Moon, Timer, Globe, Copy, Headphones,
    UserCheck, Heart,
};

const AchievementIcon: React.FC<{ name: string; size?: number; className?: string }> = ({ name, size = 28, className }) => {
    const Icon = ICON_MAP[name] || Trophy;
    return <Icon size={size} strokeWidth={1.75} className={className} />;
};

// Tons pastel do Design 2.0, um por conquista (em ciclo)
const TINTS = ['bg-tint-butter', 'bg-tint-lavender', 'bg-tint-sage', 'bg-tint-peach', 'bg-tint-sky', 'bg-tint-rose'];
const TINTS_DEEP = ['bg-tintd-butter', 'bg-tintd-lavender', 'bg-tintd-sage', 'bg-tintd-peach', 'bg-tintd-sky', 'bg-tintd-rose'];

// ── Level dots ────────────────────────────────────────────────────────────────

const LevelDots: React.FC<{ level: number; size?: 'sm' | 'lg' }> = ({ level, size = 'sm' }) => (
    <div className="flex items-center gap-1" aria-label={`${level}/5`}>
        {Array.from({ length: 5 }).map((_, i) => (
            <span
                key={i}
                className={`rounded-full transition-all duration-500 ${size === 'lg' ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5'} ${i < level ? 'bg-ink' : 'bg-ink/10'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
            />
        ))}
    </div>
);

// ── Achievement Card (expande ao clicar) ─────────────────────────────────────

const AchievementCard: React.FC<{
    achievement: UserAchievement;
    index: number;
    isOpen: boolean;
    onToggle: () => void;
}> = ({ achievement, index, isOpen, onToggle }) => {
    const { t } = useTranslation();
    const { def, level, progress } = achievement;
    const isLocked = level === 0;
    const isMax = level === 5;

    const nextThreshold = getNextThreshold(def, level);
    const barMax = nextThreshold ?? def.thresholds[4];
    const barCurrent = isMax ? barMax : Math.min(progress, barMax);
    const barPct = Math.min(100, Math.round((barCurrent / barMax) * 100));
    const unit = t(`achievements.${def.key}.unit`, def.unit);
    const tint = TINTS[index % TINTS.length];
    const tintDeep = TINTS_DEEP[index % TINTS_DEEP.length];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE, delay: Math.min(index * 0.03, 0.3) }}
            className={`bg-surface rounded-[24px] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden transition-colors duration-300 ${isOpen ? '' : 'hover:bg-tint-stone/40'}`}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                className="group w-full flex items-center gap-4 p-4 text-left"
            >
                <span className={`relative w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-ink transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105 group-hover:-rotate-6 ${isLocked ? 'bg-tint-stone opacity-60' : tint}`}>
                    <AchievementIcon name={def.lucideIcon} size={20} />
                    {isLocked && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center">
                            <Lock size={10} strokeWidth={2} className="text-muted" />
                        </span>
                    )}
                </span>

                <span className="flex-1 min-w-0">
                    <span className={`block text-[15px] font-medium tracking-[-0.01em] truncate ${isLocked ? 'text-muted' : 'text-ink'}`}>
                        {t(`achievements.${def.key}.name`, def.name)}
                    </span>
                    <span className="mt-2 flex items-center gap-3">
                        <span className="flex-1 h-[3px] rounded-full bg-ink/[0.07] overflow-hidden">
                            <motion.span
                                className={`block h-full rounded-full ${isMax ? 'bg-warn' : tintDeep}`}
                                initial={{ width: 0 }}
                                animate={{ width: `${barPct}%` }}
                                transition={{ duration: 1, ease: EASE, delay: 0.15 + Math.min(index * 0.03, 0.3) }}
                            />
                        </span>
                        <LevelDots level={level} />
                    </span>
                </span>

                <ChevronDown size={16} strokeWidth={1.75} className={`shrink-0 text-muted transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isOpen ? 'rotate-180' : 'group-hover:translate-y-0.5'}`} />
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                    >
                        <div className="px-4 pb-5 pt-1 ml-16 space-y-4">
                            <p className="text-[13px] leading-relaxed text-muted">
                                {t(`achievements.${def.key}.description`, def.description)}
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="rounded-2xl bg-tint-stone/60 px-3.5 py-3">
                                    <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted">{t('achievements.realProgress', 'Progresso')}</p>
                                    <p className="mt-1 text-sm text-ink tabular-nums">{progress.toLocaleString()} <span className="text-muted">{unit}</span></p>
                                </div>
                                <div className="rounded-2xl bg-tint-stone/60 px-3.5 py-3">
                                    <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted">
                                        {isMax ? t('achievements.max_level', 'Nível máximo') : t('achievements.nextLevel', 'Próximo nível')}
                                    </p>
                                    <p className="mt-1 text-sm text-ink tabular-nums">
                                        {isMax ? '5 / 5' : <>{barMax.toLocaleString()} <span className="text-muted">{unit}</span></>}
                                    </p>
                                </div>
                            </div>
                            {/* Os 5 níveis com os limites de cada um */}
                            <div className="flex items-center gap-1.5">
                                {def.thresholds.map((th, i) => (
                                    <div key={i} className="flex-1 text-center">
                                        <div className={`h-1 rounded-full ${i < level ? 'bg-ink' : 'bg-ink/10'}`} />
                                        <p className={`mt-1.5 font-mono text-[9px] tabular-nums ${i < level ? 'text-ink' : 'text-muted'}`}>{th.toLocaleString()}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// ── Level-Up Modal ────────────────────────────────────────────────────────────

const LevelUpModal: React.FC<{
    def: AchievementDef;
    newLevel: number;
    onClose: () => void;
}> = ({ def, newLevel, onClose }) => {
    const { t } = useTranslation();
    return (
    <div
        className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
    >
        <div
            className="bg-surface rounded-[28px] p-8 md:p-10 border border-black/[0.06] dark:border-white/[0.06] max-w-sm w-full text-center animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500"
            onClick={e => e.stopPropagation()}
        >
            <div className="relative w-20 h-20 mx-auto">
                <span className="absolute inset-0 rounded-full bg-tint-butter animate-ping opacity-40" />
                <span className="relative w-20 h-20 rounded-full bg-tint-butter text-ink flex items-center justify-center">
                    <PartyPopper size={30} strokeWidth={1.5} className="animate-in spin-in-90 zoom-in-50 duration-700" />
                </span>
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
                {t('achievements.levelReached', 'Nível {{level}} Alcançado!').replace('{{level}}', String(newLevel))}
            </p>
            <h3 className="mt-2 font-display text-2xl font-light tracking-[-0.04em] text-ink">
                {t(`achievements.${def.key}.name`, def.name)}<span className="text-primary">.</span>
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed text-muted">{t(`achievements.${def.key}.description`, def.description)}</p>

            <div className="mt-5 flex justify-center"><LevelDots level={newLevel} size="lg" /></div>

            <button
                onClick={onClose}
                className="mt-8 w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300"
            >
                {t('common.awesome', 'Incrível!')}
            </button>
        </div>
    </div>
    );
};

// ── Main AchievementsTab ──────────────────────────────────────────────────────

interface Props {
    userId: string;
}

const AchievementsTab: React.FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<UserAchievement[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [levelUpEvent, setLevelUpEvent] = useState<{ def: AchievementDef; newLevel: number } | null>(null);
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');

    // ── Load cached DB data ─────────────────────────────────────────────────────
    const load = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        const cacheKey = `libritui_cached_achievements_${userId}`;

        // ── Phase 1: Load from cache immediately ─────────────────────────────
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                setData(parsed);
                setLoading(false);
                setIsSyncing(true); // Flag that we're still checking for updates
            } catch (e) {
                console.warn("Achievements: Cache parse error", e);
                setLoading(true);
            }
        } else {
            setLoading(true);
        }

        try {
            // Recalcula a partir da atividade real antes de ler (antes, nada gravava o progresso)
            const levelUps = await syncAchievements(userId);
            const achievements = await loadAchievements(userId);

            // ── Phase 2: Update state and cache ──────────────────────────────────
            setData(achievements);
            localStorage.setItem(cacheKey, JSON.stringify(achievements));
            if (levelUps.length > 0) {
                const def = ACHIEVEMENTS.find(a => a.key === levelUps[0].key);
                if (def) setLevelUpEvent({ def, newLevel: levelUps[0].newLevel });
                soundService.play('mission');
            }
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    // ── Helper: update achievement and sync state ─────────────────────────────
    const applyProgress = async (
        uid: string,
        achievements: UserAchievement[],
        key: string,
        newProgress: number
    ): Promise<UserAchievement[]> => {
        const def = ACHIEVEMENTS.find(a => a.key === key)!;
        const newLevel = getLevelFromProgress(def, newProgress);
        const existing = achievements.find(a => a.def.key === key);
        const oldLevel = existing?.level ?? 0;

        if (newProgress > (existing?.progress ?? 0)) {
            await trackAchievement(uid, key as any, newProgress);
            if (newLevel > oldLevel) {
                // Queue a level-up notification (only show first one)
                setLevelUpEvent(prev => prev ?? { def, newLevel });
            }
        }

        return achievements.map(a =>
            a.def.key === key ? { ...a, level: newLevel, progress: newProgress } : a
        );
    };
    void applyProgress;

    const handleLevelUpClose = () => {
        setLevelUpEvent(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-20 rounded-[24px] bg-surface border border-black/[0.06] dark:border-white/[0.06] animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
                ))}
                <p className="sr-only">{t('achievements.loading')}</p>
            </div>
        );
    }

    const earned = data.filter(a => a.level > 0).length;
    const points = data.reduce((sum, a) => sum + a.level, 0);
    const maxPoints = data.length * 5 || 1;
    const ringPct = points / maxPoints;
    const R = 34;
    const C = 2 * Math.PI * R;

    const filters: { key: typeof filter; label: string; count: number }[] = [
        { key: 'all', label: t('achievements.filterAll', 'Todas'), count: data.length },
        { key: 'unlocked', label: t('achievements.filterUnlocked', 'Desbloqueadas'), count: earned },
        { key: 'locked', label: t('achievements.filterLocked', 'Bloqueadas'), count: data.length - earned },
    ];
    const visible = data
        .map((a, index) => ({ a, index }))
        .filter(({ a }) => filter === 'all' || (filter === 'unlocked' ? a.level > 0 : a.level === 0));

    return (
        <div className="space-y-6">
            {/* Resumo: anel de pontos + contagem */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] p-5 md:p-6 flex items-center gap-5"
            >
                <div className="relative w-20 h-20 shrink-0">
                    <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
                        <circle cx="40" cy="40" r={R} fill="none" strokeWidth="5" className="stroke-ink/[0.07]" />
                        <motion.circle
                            cx="40" cy="40" r={R} fill="none" strokeWidth="5" strokeLinecap="round"
                            className="stroke-tintd-butter"
                            strokeDasharray={C}
                            initial={{ strokeDashoffset: C }}
                            animate={{ strokeDashoffset: C * (1 - ringPct) }}
                            transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
                        />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center">
                        <Trophy size={22} strokeWidth={1.5} className="text-ink" />
                    </span>
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted flex items-center gap-2">
                        {t('achievements.pointsLabel', 'Pontos')}
                        {isSyncing && <Loader2 size={10} className="animate-spin text-primary" />}
                    </p>
                    <p className="mt-1 font-display text-4xl font-light tracking-[-0.045em] text-ink tabular-nums">
                        {points}<span className="text-muted text-xl"> / {maxPoints}</span>
                    </p>
                    <p className="mt-1 text-[13px] text-muted">
                        {String(t('achievements.unlocked', { earned, total: data.length, count: earned, defaultValue: '{{earned}} / {{total}} desbloqueadas' }))}
                    </p>
                </div>
            </motion.div>

            {/* Filtros */}
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-tint-stone/70 w-fit max-w-full overflow-x-auto">
                {filters.map(f => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        className="relative h-9 px-4 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors duration-300"
                    >
                        {filter === f.key && (
                            <motion.span layoutId="ach-filter" className="absolute inset-0 rounded-full bg-surface shadow-sm" transition={{ duration: 0.4, ease: EASE }} />
                        )}
                        <span className={`relative ${filter === f.key ? 'text-ink' : 'text-muted hover:text-ink'}`}>
                            {f.label} <span className="font-mono text-[10px] opacity-60">{f.count}</span>
                        </span>
                    </button>
                ))}
            </div>

            {/* Lista */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                {visible.map(({ a, index }) => (
                    <AchievementCard
                        key={a.def.key}
                        achievement={a}
                        index={index}
                        isOpen={openKey === a.def.key}
                        onToggle={() => setOpenKey(openKey === a.def.key ? null : a.def.key)}
                    />
                ))}
            </div>

            {/* Level-Up Modal */}
            {levelUpEvent && (
                <LevelUpModal
                    def={levelUpEvent.def}
                    newLevel={levelUpEvent.newLevel}
                    onClose={handleLevelUpClose}
                />
            )}
        </div>
    );
};

export default AchievementsTab;
